import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicLayout, { SEOHead, TrustBadges } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Send, CalendarIcon, CalendarCheck, Headphones, MessageSquare } from 'lucide-react';
import { getContactImages } from '../../data/seo-images';
import { LANGUAGES } from '../../data/seo-data';

const TOP_20_SLUGS = new Set(['spanish','mandarin','arabic','french','vietnamese','korean','russian','portuguese','tagalog','hindi','japanese','german','italian','haitian-creole','polish','urdu','cantonese','farsi','bengali','punjabi']);
const LANG_SPANISH = LANGUAGES.find(l => l.slug === 'spanish');
const LANG_TOP20 = LANGUAGES.filter(l => TOP_20_SLUGS.has(l.slug) && l.slug !== 'spanish').sort((a, b) => a.name.localeCompare(b.name));
const LANG_REST = LANGUAGES.filter(l => !TOP_20_SLUGS.has(l.slug)).sort((a, b) => a.name.localeCompare(b.name));

const COUNTRIES = [
  { code: 'US', name: 'United States' }, { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' }, { code: 'GB-ENG', name: 'England' },
  { code: 'GB-SCT', name: 'Scotland' }, { code: 'GB-WLS', name: 'Wales' }, { code: 'GB-NIR', name: 'Northern Ireland' },
];
const STATES = {
  US: ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming','District of Columbia'],
  CA: ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'],
  AU: ['Australian Capital Territory','New South Wales','Northern Territory','Queensland','South Australia','Tasmania','Victoria','Western Australia'],
  'GB-ENG': ['Bedfordshire','Berkshire','Bristol','Buckinghamshire','Cambridgeshire','Cheshire','Cornwall','County Durham','Cumbria','Derbyshire','Devon','Dorset','East Sussex','Essex','Gloucestershire','Greater London','Greater Manchester','Hampshire','Herefordshire','Hertfordshire','Isle of Wight','Kent','Lancashire','Leicestershire','Lincolnshire','Merseyside','Norfolk','North Yorkshire','Northamptonshire','Northumberland','Nottinghamshire','Oxfordshire','Rutland','Shropshire','Somerset','South Yorkshire','Staffordshire','Suffolk','Surrey','Tyne and Wear','Warwickshire','West Midlands','West Sussex','West Yorkshire','Wiltshire','Worcestershire'],
  'GB-SCT': ['Aberdeen','Dundee','Edinburgh','Glasgow','Highlands','Scottish Borders','Stirling'],
  'GB-WLS': ['Cardiff','Swansea','Newport','Wrexham','Carmarthenshire','Gwynedd','Pembrokeshire'],
  'GB-NIR': ['Antrim','Armagh','Belfast','Derry','Down','Fermanagh','Tyrone'],
};
const TIMES = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    TIMES.push(`${hour12}:${m.toString().padStart(2, '0')} ${ampm}`);
  }
}
const DURATIONS = ['15 minutes','30 minutes','1 hour','1.5 hours','2 hours','3 hours','4 hours','Half day (4+ hours)','Full day (8 hours)','Multi-day'];

function ObfuscatedEmail() {
  const [addr, setAddr] = useState('');
  useEffect(() => { const u = atob('c2VydmljZQ=='); const d = atob('aW50ZXJwcmV0ZXIuaW8='); setAddr(u + String.fromCharCode(64) + d); }, []);
  if (!addr) return <p className="font-semibold text-slate-400">Loading...</p>;
  return <a href={`mailto:${addr}`} className="font-semibold text-blue-700 hover:underline block" data-testid="contact-email-link">{addr}</a>;
}

function LanguageSelect({ value, onChange }) {
  return (
    <select value={value} onChange={onChange} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="contact-language">
      <option value="">Select a language</option>
      <option value={LANG_SPANISH.slug}>{LANG_SPANISH.name}</option>
      <optgroup label="Top Languages">{LANG_TOP20.map(l => <option key={l.slug} value={l.slug}>{l.name}</option>)}</optgroup>
      <optgroup label="All Languages">{LANG_REST.map(l => <option key={l.slug} value={l.slug}>{l.name}</option>)}</optgroup>
    </select>
  );
}

function ContactSidebar({ images }) {
  return (
    <div className="space-y-6">
      <img src={images[1]?.url} alt={images[1]?.alt} className="w-full rounded-xl object-cover h-40 mb-4" loading="lazy" />
      <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
      {[
        { icon: Phone, label: 'Phone', val: '1-855-340-8855', sub: 'Available 24/7' },
        { icon: Mail, label: 'Email', val: null, isEmail: true },
        { icon: MapPin, label: 'Headquarters', val: 'United States', sub: 'Serving US, Canada, UK & Australia' },
      ].map(c => (
        <div key={c.label} className="flex gap-3 p-4 bg-slate-50 rounded-lg">
          <c.icon className="w-5 h-5 text-blue-700 mt-0.5" />
          <div>
            <p className="text-xs text-slate-500 font-medium">{c.label}</p>
            {c.isEmail ? <ObfuscatedEmail /> : <p className="font-semibold">{c.val}</p>}
            {c.sub && <p className="text-xs text-slate-500">{c.sub}</p>}
          </div>
        </div>
      ))}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mt-8">
        <h3 className="font-bold text-blue-900 mb-2">Need an Interpreter Right Now?</h3>
        <p className="text-sm text-blue-800 mb-3">If you already have an account, log in to connect with an interpreter instantly.</p>
        <Link to="/auth"><Button className="bg-blue-700 hover:bg-blue-800 text-white w-full">Interpreter Login</Button></Link>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// BOOKING FORM — /book-interpreter
// ═══════════════════════════════════════
export function BookInterpreterPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', language: '', country: '', state: '', date: null, time: '', duration: '', service: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const images = getContactImages();
  const stateOptions = form.country ? (STATES[form.country] || []) : [];
  const stateLabel = form.country === 'US' ? 'State' : form.country === 'CA' ? 'Province' : form.country === 'AU' ? 'State/Territory' : form.country?.startsWith('GB') ? 'County/Region' : 'State/Region';

  // Bing UET conversion tracking
  useEffect(() => {
    if (window._bingUetLoaded) return;
    window._bingUetLoaded = true;
    window.uetq = window.uetq || [];
    const s = document.createElement('script');
    s.src = 'https://bat.bing.net/bat.js?ti=187251088';
    s.async = true;
    s.onload = function () {
      if (typeof window.UET === 'function') {
        var o = { ti: '187251088', enableAutoSpaTracking: true, q: window.uetq };
        window.uetq = new window.UET(o);
        window.uetq.push('pageLoad');
      }
    };
    document.head.appendChild(s);
    // Consent defaults
    setTimeout(function() {
      window.uetq = window.uetq || [];
      window.uetq.push('consent', 'default', { 'ad_storage': 'denied' });
      window.uetq.push('consent', 'update', { 'ad_storage': 'granted' });
    }, 100);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, date: form.date?.toISOString(), source: 'booking' }),
      });
    } catch (_) {}
    setSubmitted(true);
    toast.success('Booking request submitted! We will confirm within 1 business hour.');
  };

  return (
    <PublicLayout>
      <SEOHead title="Book an Interpreter | Schedule Professional Interpreting | Interpreter Services" description="Book a professional interpreter for phone, video, or on-site sessions. Select your language, date, and time." canonical="https://interpreterservices.us/book-interpreter" />
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4"><CalendarCheck className="w-8 h-8 text-blue-300" /><span className="text-blue-300 text-sm font-medium uppercase tracking-wide">Booking Form</span></div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Book an Interpreter</h1>
          <p className="text-lg text-blue-100 max-w-2xl">Schedule a professional interpreter for your upcoming session. Select your language, preferred date/time, and service type.</p>
        </div>
      </section>
      <TrustBadges />
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="booking-success">
                <CalendarCheck className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-green-800 mb-3">Booking Request Received!</h2>
                <p className="text-green-700 mb-2">We will confirm your interpreter booking within 1 business hour.</p>
                <p className="text-green-700">For immediate access, call <strong>1-855-340-8855</strong> — available 24/7.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 text-slate-900" data-testid="booking-form">
                <h2 className="text-2xl font-bold mb-2">Interpreter Booking Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Full Name *</Label><Input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="booking-name" /></div>
                  <div><Label className="text-slate-700">Email *</Label><Input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="booking-email" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Company</Label><Input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" /></div>
                  <div><Label className="text-slate-700">Phone *</Label><Input required value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="booking-phone" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Service Type *</Label>
                    <select required value={form.service} onChange={e => setForm(p => ({ ...p, service: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="booking-service">
                      <option value="">Select service type</option>
                      <option value="opi">Phone Interpreting (OPI)</option>
                      <option value="vri">Video Remote Interpreting (VRI)</option>
                      <option value="onsite">On-Site Interpreting</option>
                    </select></div>
                  <div><Label className="text-slate-700">Language *</Label><LanguageSelect value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Country *</Label>
                    <select required value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value, state: '' }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="booking-country">
                      <option value="">Select country</option>{COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select></div>
                  {stateOptions.length > 0 && <div><Label className="text-slate-700">{stateLabel}</Label>
                    <select value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900">
                      <option value="">Select {stateLabel.toLowerCase()}</option>{stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select></div>}
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div><Label className="text-slate-700">Date *</Label>
                    <Popover open={calOpen} onOpenChange={setCalOpen}><PopoverTrigger asChild>
                      <button type="button" className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900 flex items-center justify-between" data-testid="booking-date">
                        <span className={form.date ? '' : 'text-slate-400'}>{form.date ? form.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pick a date'}</span>
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                      </button></PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={form.date} onSelect={d => { setForm(p => ({ ...p, date: d })); setCalOpen(false); }} disabled={d => d < new Date(new Date().setHours(0,0,0,0))} initialFocus /></PopoverContent>
                    </Popover></div>
                  <div><Label className="text-slate-700">Time *</Label>
                    <select required value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="booking-time">
                      <option value="">Select time</option>{TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select></div>
                  <div><Label className="text-slate-700">Duration *</Label>
                    <select required value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="booking-duration">
                      <option value="">Select duration</option>{DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select></div>
                </div>
                <div><Label className="text-slate-700">Additional Notes</Label>
                  <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-900 resize-none" placeholder="Any special requirements, subject matter, or context for the interpreter..." /></div>
                <Button type="submit" size="lg" className="bg-blue-700 hover:bg-blue-800 text-white w-full sm:w-auto px-10" data-testid="booking-submit">
                  <CalendarCheck className="w-4 h-4 mr-2" />Submit Booking Request
                </Button>
              </form>
            )}
          </div>
          <div className="lg:col-span-2"><ContactSidebar images={images} /></div>
        </div>
      </div>
    </PublicLayout>
  );
}

// ═══════════════════════════════════════
// DEMO / ENQUIRY FORM — /book-demo
// ═══════════════════════════════════════
export function BookDemoPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', role: '', team_size: '', sessions_per_month: '', minutes_per_month: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const images = getContactImages();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'demo' }),
      });
    } catch (_) {}
    setSubmitted(true);
    toast.success('Demo request received! Our team will reach out to schedule your session.');
  };

  return (
    <PublicLayout>
      <SEOHead title="Book a Demo | See Interpreter Services in Action | Interpreter Services" description="See Interpreter Services in action. Schedule a personalized demo with our team." canonical="https://interpreterservices.us/book-demo" />
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4"><MessageSquare className="w-8 h-8 text-blue-300" /><span className="text-blue-300 text-sm font-medium uppercase tracking-wide">Enquiry Form</span></div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Book a Demo</h1>
          <p className="text-lg text-blue-100 max-w-2xl">See how Interpreter Services can transform language access for your organization. Our team will walk you through the platform, answer your questions, and help you find the right plan.</p>
        </div>
      </section>
      <TrustBadges />
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="demo-success">
                <MessageSquare className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-green-800 mb-3">Demo Request Received!</h2>
                <p className="text-green-700 mb-2">Our team will reach out within 1 business day to schedule your personalized demo.</p>
                <p className="text-green-700">In the meantime, explore our <Link to="/how-it-works" className="underline font-semibold">How It Works</Link> page.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 text-slate-900" data-testid="demo-form">
                <h2 className="text-2xl font-bold mb-2">Tell Us About Your Organization</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Full Name *</Label><Input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="demo-name" /></div>
                  <div><Label className="text-slate-700">Work Email *</Label><Input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="demo-email" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Company / Organization *</Label><Input required value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="demo-company" /></div>
                  <div><Label className="text-slate-700">Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Your Role</Label>
                    <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="demo-role">
                      <option value="">Select your role</option>
                      <option value="executive">Executive / C-Suite</option>
                      <option value="director">Director / VP</option>
                      <option value="manager">Manager</option>
                      <option value="coordinator">Language Access Coordinator</option>
                      <option value="procurement">Procurement</option>
                      <option value="other">Other</option>
                    </select></div>
                  <div><Label className="text-slate-700">Team Size</Label>
                    <select value={form.team_size} onChange={e => setForm(p => ({ ...p, team_size: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="demo-team-size">
                      <option value="">Select team size</option>
                      <option value="1-10">1-10 people</option>
                      <option value="11-50">11-50 people</option>
                      <option value="51-200">51-200 people</option>
                      <option value="201-1000">201-1,000 people</option>
                      <option value="1001+">1,001+ people</option>
                    </select></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Interpreter Sessions per Month</Label>
                    <select value={form.sessions_per_month} onChange={e => setForm(p => ({ ...p, sessions_per_month: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="demo-sessions-per-month">
                      <option value="">Select sessions per month</option>
                      <option value="1-10">1-10 sessions</option>
                      <option value="11-50">11-50 sessions</option>
                      <option value="51-200">51-200 sessions</option>
                      <option value="201-500">201-500 sessions</option>
                      <option value="501-1000">501-1,000 sessions</option>
                      <option value="1001+">1,001+ sessions</option>
                    </select></div>
                  <div><Label className="text-slate-700">Interpreter Minutes per Month</Label>
                    <select value={form.minutes_per_month} onChange={e => setForm(p => ({ ...p, minutes_per_month: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="demo-minutes-per-month">
                      <option value="">Select minutes per month</option>
                      <option value="1-100">1-100 minutes</option>
                      <option value="101-500">101-500 minutes</option>
                      <option value="501-2000">501-2,000 minutes</option>
                      <option value="2001-5000">2,001-5,000 minutes</option>
                      <option value="5001-20000">5,001-20,000 minutes</option>
                      <option value="20001+">20,001+ minutes</option>
                    </select></div>
                </div>
                <div><Label className="text-slate-700">What are you looking to solve? *</Label>
                  <textarea required value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={4} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-900 resize-none" placeholder="Tell us about your current interpreting challenges, volume, languages needed, and what you'd like to see in the demo..." data-testid="demo-message" /></div>
                <Button type="submit" size="lg" className="bg-blue-700 hover:bg-blue-800 text-white w-full sm:w-auto px-10" data-testid="demo-submit">
                  <Send className="w-4 h-4 mr-2" />Request Demo
                </Button>
              </form>
            )}
          </div>
          <div className="lg:col-span-2"><ContactSidebar images={images} /></div>
        </div>
      </div>
    </PublicLayout>
  );
}

// ═══════════════════════════════════════
// GENERAL CONTACT FORM — /contact
// ═══════════════════════════════════════
export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', language: '', country: '', state: '', date: null, time: '', duration: '', service: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const images = getContactImages();
  const stateOptions = form.country ? (STATES[form.country] || []) : [];
  const stateLabel = form.country === 'US' ? 'State' : form.country === 'CA' ? 'Province' : form.country === 'AU' ? 'State/Territory' : form.country?.startsWith('GB') ? 'County/Region' : 'State/Region';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, date: form.date?.toISOString(), source: 'booking' }),
      });
    } catch (_) {}
    setSubmitted(true);
    toast.success('Booking request submitted! We will confirm within 1 business hour.');
  };

  return (
    <PublicLayout>
      <SEOHead title="Contact Us | Get a Quote | Interpreter Services" description="Contact Interpreter Services for questions, quotes, or support. Our team responds within 1 business hour." canonical="https://interpreterservices.us/contact" />
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-lg text-blue-100 max-w-2xl">Have a question? Need a quote? Our team responds within 1 business hour.</p>
          </div>
          <div className="hidden lg:block">
            <img src={images[0]?.url} alt={images[0]?.alt} className="rounded-2xl shadow-lg w-full object-cover h-56" loading="eager" />
          </div>
        </div>
      </section>
      <TrustBadges />

      {/* Quick action cards */}
      <div className="max-w-6xl mx-auto px-6 -mt-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/contact" className="flex items-center gap-3 p-4 bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow" data-testid="quick-send-message">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0"><MessageSquare className="w-5 h-5 text-white" /></div>
            <div><p className="font-semibold text-sm">Send Us a Message</p><p className="text-xs text-slate-500">Get in touch with our team</p></div>
          </Link>
          <Link to="/book-demo" className="flex items-center gap-3 p-4 bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow" data-testid="quick-book-demo">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0"><CalendarCheck className="w-5 h-5 text-white" /></div>
            <div><p className="font-semibold text-sm">Book a Demo</p><p className="text-xs text-slate-500">See the platform in action</p></div>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="contact-success">
                <CalendarCheck className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-green-800 mb-3">Booking Request Received!</h2>
                <p className="text-green-700 mb-2">We will confirm your interpreter booking within 1 business hour.</p>
                <p className="text-green-700">For immediate interpreter access, call <strong>1-855-340-8855</strong> — available 24/7.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 text-slate-900" data-testid="contact-form">
                <h2 className="text-2xl font-bold mb-2">Book an Interpreter</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Full Name *</Label><Input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="contact-name" /></div>
                  <div><Label className="text-slate-700">Email *</Label><Input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="contact-email" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Company</Label><Input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" /></div>
                  <div><Label className="text-slate-700">Phone *</Label><Input required value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="contact-phone" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Service Type *</Label>
                    <select required value={form.service} onChange={e => setForm(p => ({ ...p, service: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="contact-service">
                      <option value="">Select service type</option>
                      <option value="opi">Phone Interpreting (OPI)</option>
                      <option value="vri">Video Remote Interpreting (VRI)</option>
                      <option value="onsite">On-Site Interpreting</option>
                    </select></div>
                  <div><Label className="text-slate-700">Language *</Label><LanguageSelect value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700">Country *</Label>
                    <select required value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value, state: '' }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="contact-country">
                      <option value="">Select country</option>{COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select></div>
                  {stateOptions.length > 0 && <div><Label className="text-slate-700">{stateLabel}</Label>
                    <select value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900">
                      <option value="">Select {stateLabel.toLowerCase()}</option>{stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select></div>}
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div><Label className="text-slate-700">Date *</Label>
                    <Popover open={calOpen} onOpenChange={setCalOpen}><PopoverTrigger asChild>
                      <button type="button" className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900 flex items-center justify-between" data-testid="contact-date">
                        <span className={form.date ? '' : 'text-slate-400'}>{form.date ? form.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pick a date'}</span>
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                      </button></PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={form.date} onSelect={d => { setForm(p => ({ ...p, date: d })); setCalOpen(false); }} disabled={d => d < new Date(new Date().setHours(0,0,0,0))} initialFocus /></PopoverContent>
                    </Popover></div>
                  <div><Label className="text-slate-700">Time *</Label>
                    <select required value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="contact-time">
                      <option value="">Select time</option>{TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select></div>
                  <div><Label className="text-slate-700">Duration *</Label>
                    <select required value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} className="w-full mt-1 h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="contact-duration">
                      <option value="">Select duration</option>{DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select></div>
                </div>
                <div><Label className="text-slate-700">Additional Notes</Label>
                  <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-900 resize-none" placeholder="Any special requirements, subject matter, or context for the interpreter..." data-testid="contact-message" /></div>
                <Button type="submit" size="lg" className="bg-blue-700 hover:bg-blue-800 text-white w-full sm:w-auto px-10" data-testid="contact-submit">
                  <CalendarCheck className="w-4 h-4 mr-2" />Submit Booking Request
                </Button>
              </form>
            )}
          </div>
          <div className="lg:col-span-2"><ContactSidebar images={images} /></div>
        </div>
      </div>
    </PublicLayout>
  );
}
